import { environment } from '../../../environments/environment';

/**
 * Runtime-Override für `environment.dataSource` (Sprint 6 S6-2).
 *
 * Default seit Sprint 5c: `dataSource: 'http'` (App fährt gegen das
 * Laravel-Backend). Für Playwright-Specs, die noch gegen die Dummy-
 * Facades laufen (Admin-Demos vor Sprint-6-Admin-V4-Refactor, plus
 * Sprint-2/3-Customer-Demos), reicht ein `?dataSource=dummy` an einer
 * beliebigen Route — der Override bleibt für die Session erhalten, weil
 * er in `sessionStorage` persistiert wird. Tab-Close räumt ihn auf (wie
 * Auth-Tokens).
 *
 * `authMode` wird gleichzeitig mit `dataSource` umgeschaltet — Dummy-
 * Auth gehört zu Dummy-Daten, HTTP-Auth zu HTTP-Daten. Mixed-Mode war
 * nie ein Anwendungsfall.
 *
 * Reihenfolge:
 *   1. `?dataSource=...` in der URL — gewinnt immer, schreibt sessionStorage
 *   2. sessionStorage — überlebt Navigation/Routing innerhalb der Session
 *   3. `environment.dataSource` — Build-Time-Default
 */

export type DataSource = 'dummy' | 'http';

const STORAGE_KEY = 'gold.runtime.dataSource';

function readFromQuery(): DataSource | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const v = url.searchParams.get('dataSource');
    return v === 'dummy' || v === 'http' ? v : null;
  } catch {
    return null;
  }
}

function readFromStorage(): DataSource | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    return v === 'dummy' || v === 'http' ? v : null;
  } catch {
    return null;
  }
}

function persist(value: DataSource): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage-Quota / Privacy-Mode — Override gilt dann nur für diesen
    // Module-Load, nicht über Navigation hinweg. Akzeptabel.
  }
}

/**
 * Entscheidet, welche Facade-Implementierung (Dummy vs. HTTP) für die
 * laufende Session aktiv ist. Wird beim Bootstrap aus `app.config.ts`
 * aufgerufen — also vor jeder Provider-Bindung.
 */
export function resolveRuntimeDataSource(): DataSource {
  const fromQuery = readFromQuery();
  if (fromQuery) {
    persist(fromQuery);
    return fromQuery;
  }
  const fromStorage = readFromStorage();
  if (fromStorage) {
    return fromStorage;
  }
  return environment.dataSource as DataSource;
}
